<script setup lang="tsx">
import {
  StyleValue,
  onMounted,
  onUnmounted,
  ref,
  watch,
  watchEffect
} from "vue";
import { throttle } from "@zyzgroup/core-common";
import {
  getInnerText,
  useClipboard,
  tap$,
  DrawShape,
  DrawType,
  DrawContext,
  requestAnimationFrameCycle
} from "@zyzgroup/core-web";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {},
  inheritAttrs: true
});

export type DisplayType = "full" | "shrink" | "empty";

const props = withDefaults(
  defineProps<{
    type?: DrawType;
    shapes?: DrawShape[]; // const shapes = ref([]);
    loop?: boolean;
    enableCustomContext?: boolean;
    supportCopyToClipboard?: boolean; // 是否支持复制文字
    displayType?: DisplayType;
    enableClickEffect?: boolean; // 点击时是否添加遮罩
    clickedCoverColor?: string; // 点击时增加的遮罩颜色
  }>(),
  {
    type: "div",
    shapes: () => [],
    loop: false,
    enableCustomContext: false,
    supportCopyToClipboard: false,
    displayType: "full",
    enableClickEffect: false,
    clickedCoverColor: "rgba(0,0,0,0.1)"
  }
);

const emits = defineEmits<{
  (event: "mount", box: { el: HTMLElement; rect: DOMRect }): void;
  (event: "resize", box: { el: HTMLElement; rect: DOMRect }): void;
  (
    event: "start",
    drawContext: DrawContext,
    box: { el: HTMLElement; rect: DOMRect }
  ): void;
  (
    event: "frame",
    drawContext: DrawContext,
    box: { el: HTMLElement; rect: DOMRect }
  ): void;
  (
    event: "end",
    drawContext: DrawContext,
    box: { el: HTMLElement; rect: DOMRect }
  ): void;
  (event: "copyToClipboardSuccess", text: string): void;
  (event: "copyToClipboardError", err: string): void;
  (event: "throttleClick", e: Event): void;
}>();

const r = ref();

let drawContext;
let resizeSubscription;
let tapSubscription;
let cancelAnimationFrame;

let isEmitMount = false;

let isShapesUpdated = true;
// props.shapes 地址更新时，重新 emits("start")
watch(
  () => props.shapes,
  (newVal, oldVal) => {
    isShapesUpdated = newVal !== oldVal;
    if (!drawContext) {
      return;
    }
    if (!r.value) {
      return;
    }
    if (isShapesUpdated) {
      startAnimationFrame(drawContext, {
        el: r.value,
        rect: drawContext.rect
      });
    }
  }
);

function startAnimationFrame(drawContext, box) {
  cancelAnimationFrame?.();
  emits("start", drawContext, box);
  cancelAnimationFrame = requestAnimationFrameCycle(() => {
    emits("frame", drawContext, box);
    // if (props.shapes.length) {
    // if (isShapesUpdated) {
    drawContext.draw(...props.shapes);
    // } else {
    //   drawContext.draw(); // 传入空数组：保持原 shapes 以提高性能
    // }
    // } else {
    //   drawContext.clear();
    //   cancelAnimationFrame?.();
    //   emits("end", drawContext, box);
    // }
    isShapesUpdated = false;
    return props.loop;
  });
}

watchEffect(() => {
  if (!r.value) {
    // not mounted yet, or the element was unmounted (e.g. by v-if)
    return;
  }

  if (!drawContext) {
    drawContext = new DrawContext(
      r.value,
      props.type,
      props.enableCustomContext
    );
    resizeSubscription = drawContext.size$.subscribe((rect) => {
      if (rect) {
        const box = { el: r.value, rect };
        if (!isEmitMount) {
          isEmitMount = true;
          emits("mount", box);
        }
        emits("resize", box);
        startAnimationFrame(drawContext, box);
      }
    });
  }

  if (props.supportCopyToClipboard && !tapSubscription) {
    tapSubscription = tap$(r.value).subscribe(() => {
      const text = getInnerText(r.value);
      useClipboard(text)
        .then(() => {
          emits("copyToClipboardSuccess", text);
        })
        .catch((err) => {
          emits("copyToClipboardError", err.toString());
        });
    });
  }
});

const isClicked = ref(false);
const throttleClick = throttle((e: Event) => {
  isClicked.value = true;
  setTimeout(() => {
    isClicked.value = false;
    emits("throttleClick", e);
    //  const dataset = (e.target as HTMLElement).dataset;
  }, 100);
  // const dataset = (e.currentTarget as HTMLElement).dataset;
}, 300);

onMounted(() => {});

onUnmounted(() => {
  cancelAnimationFrame?.();
  resizeSubscription?.unsubscribe();
  tapSubscription?.unsubscribe();
  drawContext?.destroy();
});

// 不推荐使用
defineExpose({
  getElement: () => r.value,
  getRect: () => r.value.getBoundingClientRect()
});
</script>

<template>
  <!-- v-bind="$attrs" -->
  <div
    ref="r"
    class="box"
    :style="
      Object.assign(
        props.displayType == 'empty' ? {} : {
          position: 'relative',
          display: props.displayType == 'shrink' ? 'inline-block' : 'block',
          width: props.displayType == 'shrink' ? 'auto' : '100%',
          height: props.displayType == 'shrink' ? 'auto' : '100%',
          boxSizing: 'border-box',
          /* overflow: 'hidden', */
          /* pointer-events: 'none', */
          /* cursor: 'pointer' */
        },
        props.enableClickEffect
          ? {
            position: 'relative',
            userSelect: 'none',
          }
          : {
            userSelect: 'auto',
          },
      ) as StyleValue
    "
    @click="throttleClick"
  >
    <slot></slot>
    <div
      v-if="props.enableClickEffect"
      :style="[
        `display: ${isClicked ? 'block' : 'none'}`,
        `position: absolute`,
        `top: 0`,
        `left: 0`,
        `width: 100%`,
        `height: 100%`,
        `background: ${props.clickedCoverColor}`,
        `z-index: 1`
      ]"
    ></div>
  </div>
</template>
